import { Auth } from "@lib/authentication";
import { parseAuthResult } from "@lib/authentication/amplify/amplify.helpers";
import type { MemberHomeArm } from "@lib/experiments/memberHome";

import { omgApiClient } from "./omgApiClient";

export interface MemberExperimentsResponse {
  /** Collections Home A/B test arm (OMG-2581); null = not in the test. */
  memberHome: MemberHomeArm | null;
  /** True when memberHome is this member's own frozen assignment, false when
   *  it is a post-test winner forced for everyone (or null). Gates the
   *  analytics trait: only real enrolments are stamped. */
  memberHomeAssigned: boolean;
}

/**
 * Upper bound on the WHOLE call, token refresh included. /home holds a loader
 * for English members until this settles, so it must settle: the axios
 * timeout alone would leave `Auth.currentAuthenticatedUser()` (a Cognito
 * refresh over a black-holed connection) unbounded in front of it. Past the
 * deadline the query errors, the arm reads null and the member gets today's
 * home (fail open). Shorter than getMe's 15s on purpose: this request gates
 * a nicety, entitlements gate access.
 */
export const MEMBER_EXPERIMENTS_DEADLINE_MS = 6_000;

const withDeadline = <T>(work: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () =>
        reject(new Error(`experiments/member did not settle within ${ms}ms`)),
      ms,
    );
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

const requestMemberExperiments = async (
  locale: string,
): Promise<MemberExperimentsResponse> => {
  const { accessToken } = parseAuthResult(
    await Auth.currentAuthenticatedUser(),
  );

  const response = await omgApiClient.get<MemberExperimentsResponse>(
    "/experiments/member",
    {
      headers: { Authorization: `Bearer ${accessToken?.jwt}` },
      params: { locale },
      timeout: 5_000,
    },
  );

  return response.data;
};

/**
 * Member-grain experiment arms for the signed-in member. `locale` is the
 * app's SETTLED UI language (LocaleContext.settledLocale), an eligibility
 * input the server freezes on for the English-only test; the server never
 * trusts it for anything else. Rejects (never hangs) past the deadline.
 */
export const fetchMemberExperiments = (
  locale: string,
): Promise<MemberExperimentsResponse> =>
  withDeadline(
    requestMemberExperiments(locale),
    MEMBER_EXPERIMENTS_DEADLINE_MS,
  );
